import { ApolloProvider } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { init as initCrikket } from "@toppynl/crikket-capture";
import { ThemeProvider } from "next-themes";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import { PermissionProvider } from "@/lib/authorization";
import { env } from "@/lib/env";
import { MobileAppLayoutProvider } from "@/lib/providers/mobile-app-layout-context";
import { apolloClient } from "./lib/apollo/apollo-client.ts";
import { router } from "./router.tsx";
import "./lib/i18n/config"; // Initialize i18n
import "./globals.css";
import { Toaster } from "./components/ui/sonner.tsx";
import setupFirebase from "./firebase.ts";
import { AppUpdaterService } from "./lib/services/app-updater-service.ts";
import { StatusBarService } from "./lib/services/status-bar-service.ts";

// Initialize Sentry for error tracking
if (env.sentryDsn) {
  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.isProd ? "production" : "development",
    // Only send errors in production to avoid noise during development
    beforeSend(event) {
      return env.isProd ? event : null;
    },
  });
}

// Initialize Crikket bug reporting widget. Gated solely by the key — only
// staging's build config supplies it, so prod builds skip init at runtime.
if (import.meta.env.VITE_CRIKKET_KEY) {
  initCrikket({
    key: import.meta.env.VITE_CRIKKET_KEY,
    host: import.meta.env.VITE_CRIKKET_HOST ?? "https://crikket.internal.96digital.de",
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

setupFirebase();

// Initialize app updater service
AppUpdaterService.getInstance().initialize();

// Initialize status bar (only works on native platforms)
StatusBarService.getInstance().initialize();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <PermissionProvider>
            <MobileAppLayoutProvider>
              <RouterProvider router={router} />
              <Toaster richColors position="bottom-right" />
            </MobileAppLayoutProvider>
          </PermissionProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ApolloProvider>
  </React.StrictMode>
);
